import { Button, DataTable, HorizontalStack, InlineCode, List, Text, Layout, LegacyCard, TextField, VerticalStack } from '@shopify/polaris';
import type { ClientApplication } from '@shopify/app-bridge';
import Firebase from '../../contexts/firebase';
import { useContext, useCallback, useState } from 'react';
import { ToastContext } from '../../contexts/ToastContext';
import { ModalContext } from '../../contexts/ModalContext';
import './index.css';

export const ImplementationInstructions = (props: { firebase: Firebase, app: ClientApplication }) => {
  const { firebase } = props;
  const { dispatchToast } = useContext(ToastContext);
  const { dispatchModal } = useContext(ModalContext);

  const [isFetchingSecret, setIsFetchingSecret] = useState<boolean>(false);
  const [isGeneratingSecret, setIsGeneratingSecret] = useState<boolean>(false);
  const [signatureSecret, setSignatureSecret] = useState<string>("");

  // set title and description
  const title = "Implementation instructions";
  const description = "Since the app can update customer data we need to assure that all updates made by the storefront are secure. Please follow the implementation instructions.";

  const getToken = useCallback(async () => {
    try {
      setIsFetchingSecret(true);

      // Get the signature secret
      const fetchUrl = `${process.env.REACT_APP_CLOUD_FUNCTIONS_URI}/AdminHelper/fetch-signature-secret`;
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-shopify-auth-token': firebase.userData.token || "",
        },
      };

      // Execute login fetch
      const authResponse = await fetch(fetchUrl, fetchOptions);
      if (!authResponse.ok) throw new Error(`Fetch signature secret status ${authResponse.status}`);

      // Get response body
      const authResponseBody: TSignatureResponse = await authResponse.json();

      setSignatureSecret(authResponseBody.secret);
    } catch (error) {
      dispatchToast("Unable to fetch the signature secret, please refresh and try again", { isError: true });
    } finally {
      setIsFetchingSecret(false);
    }

  }, [firebase.userData.token, dispatchToast]);

  const generateToken = useCallback(async () => {
    try {
      dispatchModal(false);
      setIsGeneratingSecret(true);

      // Get the signature secret
      const fetchUrl = `${process.env.REACT_APP_CLOUD_FUNCTIONS_URI}/AdminHelper/generate-signature-secret`;
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-shopify-auth-token': firebase.userData.token || "",
        },
      };

      // Execute login fetch
      const authResponse = await fetch(fetchUrl, fetchOptions);
      if (!authResponse.ok) throw new Error(`Fetch signature secret status ${authResponse.status}`);

      // Get response body
      const authResponseBody: TSignatureResponse = await authResponse.json();

      setSignatureSecret(authResponseBody.secret);
    } catch (error) {
      dispatchToast("Unable to generate the signature secret, please refresh and try again", { isError: true });
    } finally {
      setIsGeneratingSecret(false);
    }

  }, [firebase.userData.token, dispatchToast, dispatchModal]);

  const confirmGenerateToken = useCallback(() => {
    dispatchModal(true, {
      title: "Are you sure?",
      message: "This will break your implementation, you need to update your implementation when you change the secret",
      primaryAction: () => generateToken(),
      primaryActionLabel: "Generate new token",
      isPrimaryDestructive: true,
    });

  }, [dispatchModal, generateToken]);

  return (
    <Layout.AnnotatedSection title={title} description={description}>
      <LegacyCard title="Implementation instructions">
        <LegacyCard.Section title="Add / remove a tag">
          <List type="number">
            <List.Item>
              Make a POST request to this endpoint if you want to add a tag:{" "}
              <br />
              <Text as="span">
                <InlineCode>
                  {process.env.REACT_APP_CLOUD_FUNCTIONS_URI}
                  /CustomerData/customers/tags/add
                </InlineCode>
              </Text>
              <br />
              <br />
              Make a POST request to this endpoint if you want to remove a tag:{" "}
              <br />
              <Text as="span">
                <InlineCode>
                  {process.env.REACT_APP_CLOUD_FUNCTIONS_URI}
                  /CustomerData/customers/tags/remove
                </InlineCode>
              </Text>
            </List.Item>
            <List.Item>
              Ensure you add a JSON Payload as Post data. You need 2 elements in
              here:
              <DataTable
                hoverable={false}
                columnContentTypes={["text", "text", "text"]}
                headings={[]}
                rows={[
                  [
                    "customer",
                    "number",
                    "The numeric part of the ID (not the GraphQL GID)",
                  ],
                  ["tags", "array", "Array with tags to add or remove"],
                ]}
              />
              <p>An example of how this might look is shown below:</p>
              <pre className="inline-code">
                {`{
  "customer": "123456789",
  "tags": ["TEST:TEST"]
}`}
              </pre>
            </List.Item>
            <List.Item>
              <p>
                Ensure you add all the necessary headers to let us know you
                provide a JSON object. Furthermore you need to provide 2
                additional headers named{" "}
                <Text as="span">
                  <InlineCode>X-Shopify-Shop-Domain</InlineCode>
                </Text>{" "}
                and{" "}
                <Text as="span">
                  <InlineCode>X-Shopify-HMAC-SHA256</InlineCode>
                </Text>
                . For the Shop domain you need to enter the 'myshopify.com'
                domain name. For the HMAC-SHA256, the value should be generated
                in Liquid:
                <pre className="inline-code">{`{{ customer.id | hmac_sha256: 'SIGNATURE-SECRET' }}`}</pre>
                The signature secret can be found below.
              </p>
              <p>An example of how this might look is shown below:</p>
              <pre className="inline-code">
                {`X-Shopify-Shop-Domain: trimtex-b2c.myshopify.com
X-Shopify-HMAC-SHA256: 013c18e296c0565e7e2c83e512d32eeb5f9b0e2b1a7c51ba702b59dc57903c55`}
              </pre>
            </List.Item>
            <List.Item>
              <p>
                Response is always in JSON format and always contains a "status"
                element. This can be either "ok", "unauthorized" or "error".
              </p>
              <p>Example of a succesfull message</p>
              <pre className="inline-code">{`{"status":"ok","message":"Successfully removed tags"}`}</pre>
              <p>Example of a failure (in this case a validation that fails)</p>
              <pre className="inline-code">{`{"status":"unauthorized","message":"Unable to validate the request."}`}</pre>
            </List.Item>
          </List>
        </LegacyCard.Section>
        <LegacyCard.Section title="Manage Metafields">
          <List type="number">
            <List.Item>
              Make a POST request to this endpoint if you want to update or add
              a <code>single_line_text_field</code> metafield: <br />
              <Text as="span">
                <InlineCode>
                  {process.env.REACT_APP_CLOUD_FUNCTIONS_URI}
                  /CustomerData/customers/metafields/:namespace/:key/update
                </InlineCode>
              </Text>
              <br />
              <br />
              Make a POST request to this endpoint if you want to add a value to
              a single_line_text_field list metafield: <br />
              <Text as="span">
                <InlineCode>
                  {process.env.REACT_APP_CLOUD_FUNCTIONS_URI}
                  /CustomerData/customers/metafields/:namespace/:key/add
                </InlineCode>
              </Text>
              <br />
              <br />
              Make a POST request to this endpoint if you want to remove a
              metafield: <br />
              <Text as="span">
                <InlineCode>
                  {process.env.REACT_APP_CLOUD_FUNCTIONS_URI}
                  /CustomerData/customers/metafields/:namespace/:key/remove
                </InlineCode>
              </Text>
            </List.Item>
            <List.Item>
              Ensure you add a JSON Payload as Post data. You need 2 elements in
              here:
              <DataTable
                hoverable={false}
                columnContentTypes={["text", "text", "text"]}
                headings={[]}
                rows={[
                  [
                    "customer",
                    "number",
                    "The numeric part of the ID (not the GraphQL GID)",
                  ],
                  ["value", "string", "The value to set the metafield to"],
                ]}
              />
              <p>An example of how this might look is shown below:</p>
              <pre className="inline-code">
                {`{
  "customer": "123456789",
  "value": "Metafield value"
}`}
              </pre>
            </List.Item>
            <List.Item>
              <p>
                Ensure you add all the necessary headers to let us know you
                provide a JSON object. Furthermore you need to provide 2
                additional headers named{" "}
                <Text as="span">
                  <InlineCode>X-Shopify-Shop-Domain</InlineCode>
                </Text>{" "}
                and{" "}
                <Text as="span">
                  <InlineCode>X-Shopify-HMAC-SHA256</InlineCode>
                </Text>
                . For the Shop domain you need to enter the 'myshopify.com'
                domain name. For the HMAC-SHA256, the value should be generated
                in Liquid:
                <pre className="inline-code">{`{{ customer.id | append: '-' | append: 'namespace' | append: '-' | append: 'key' | hmac_sha256: 'SIGNATURE-SECRET' }}`}</pre>
                The signature secret can be found below.  Be sure to replace namespace and key with the relevant values from the request.
              </p>
              <p>An example of how this might look is shown below:</p>
              <pre className="inline-code">
                {`X-Shopify-Shop-Domain: trimtex-b2c.myshopify.com
X-Shopify-HMAC-SHA256: 013c18e296c0565e7e2c83e512d32eeb5f9b0e2b1a7c51ba702b59dc57903c55`}
              </pre>
            </List.Item>
            <List.Item>
              <p>
                Response is always in JSON format and always contains a "status"
                element. This can be either "ok", "unauthorized" or "error".
              </p>
              <p>Example of a succesfull message</p>
              <pre className="inline-code">{`{"status":"ok","message":"Successfully removed metafield"}`}</pre>
              <p>Example of a failure (in this case a validation that fails)</p>
              <pre className="inline-code">{`{"status":"unauthorized","message":"Unable to validate the request."}`}</pre>
            </List.Item>
          </List>
        </LegacyCard.Section>
        <LegacyCard.Section>
          <VerticalStack gap="1">
            <p>
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                Signature secret
              </Text>
            </p>
            <HorizontalStack gap="1">
              <div style={{ width: "320px" }}>
                <TextField
                  label=""
                  autoComplete=""
                  value={signatureSecret}
                  readOnly
                  placeholder="Press the Show secret button to expose"
                />
              </div>
              <Button
                primary
                children="Show secret"
                onClick={getToken}
                loading={isFetchingSecret}
                disabled={signatureSecret.length > 0 || isGeneratingSecret}
              />
              <Button
                destructive
                children="Renew secret"
                onClick={confirmGenerateToken}
                loading={isGeneratingSecret}
                disabled={isFetchingSecret}
              />
            </HorizontalStack>
          </VerticalStack>
        </LegacyCard.Section>
      </LegacyCard>
    </Layout.AnnotatedSection>
  );
};

type TSignatureResponse = {
  status: string;
  secret: string;
}